import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';

export type WizardType = 'T0' | 'T1' | 'T2' | 'T3' | 'T4' | 'Moradia';

export type DivisionKind =
  | 'sala' | 'cozinha' | 'quarto' | 'wc' | 'corredor'
  | 'escritorio' | 'garagem' | 'jardim' | 'outro'
  | 'casa'; // virtual — global home products (fechadura, alarme, câmaras, videoporteiro)

export interface DivisionConfig {
  id: string;
  kind: DivisionKind;
  label: string;
  optedIn: boolean;
  categoryId?: string;
}

export type Selections = Record<string, string[]>; // divisionId → SKU[]

// A guided-wizard combo: one aparelhagem point (espelho + módulo) built by
// the AddDeviceWizard popup. SKUs are mirrored into `selections` so recipe,
// colour bulk-apply and cart keep working unchanged.
export interface DeviceCombo {
  id: string;
  kind: 'interruptor' | 'tomada';
  teclas: number | null; // null for tomadas
  boxes: 1 | 2 | 3 | 4; // nº de caixas de aparelhagem (espelho size)
  color: string | null;
  label: string; // human summary, also used in the floor-plan legend
  skus: string[]; // módulo + espelho
  qty: number; // unidades do mesmo ponto (default 1)
  // Communication protocol of the chosen module(s), when known. Drives the
  // hub requirement (P0-3) and neutral validation (P0-2). 'zigbee*' → needs a
  // hub; 'zigbee-cn' → needs neutral in the box.
  tech?: import('../lib/espelhoCatalog').ModuleTech | null;
  needsHub?: boolean;
  needsNeutral?: boolean;
}

// Whether the customer's installation has a neutral wire in the back-boxes.
// 'unknown' is a first-class answer (common in old houses) → we default to the
// no-neutral-safe path and flag the lead for a technical visit.
export type NeutralAnswer = 'yes' | 'no' | 'unknown';

export type Devices = Record<string, DeviceCombo[]>; // divisionId → combos

export type InstallTier = 'material' | 'electrica' | 'config' | 'full';

// Floor-plan state (Fase B). Plan images live in IndexedDB
// (`syhouse:plan:<floorId>`) — sessionStorage can't hold photos.
export interface FloorPlacement {
  x: number; // normalized 0..1 within the plan image
  y: number;
}

// Per-room product positioning (2D editor). Room photos live in IDB
// (`syhouse:room:<divisionId>`). Positions normalized 0..1 within the photo.
// Keys are SKUs OR DeviceCombo ids (a combo is placed/dragged as one group).
export type RoomPlacements = Record<string, Record<string, FloorPlacement>>; // divId → key → {x,y}

// Space-annotation layer (#5): generic markers the customer places BEFORE
// configuring products — furniture (visualization) + existing electrical points
// / devices (survey for quoting). These are NOT Smartify products; the existing
// markers + the per-room note flow into the lead payload as context. Positions
// normalized 0..1 on the same room canvas.
export type DecorCategory = 'furniture' | 'existing';
export interface DecorItem {
  id: string;
  category: DecorCategory;
  type: string;       // palette key, e.g. 'sofa' | 'tomada'
  label: string;      // human label for the lead payload
  x: number;
  y: number;
  note?: string;      // free text (mainly for the "Outro" marker)
}
export type RoomDecor = Record<string, DecorItem[]>;       // divId → markers
export type RoomSurveyNote = Record<string, string>;       // divId → "algo que devamos saber"

export interface Floor {
  id: string;
  label: string;
  hasPlan: boolean;
  placements: Record<string, FloorPlacement>; // divisionId → position
}

interface WizardState {
  sessionId: string | null;
  // Opaque token returned when the session is created; sent back as
  // X-Session-Token on every later call so only this client can touch it.
  accessToken: string | null;
  wizardType: WizardType | null;
  installTier: InstallTier;
  hasNeutral: NeutralAnswer | null; // P0-2: explicit neutral qualification
  divisions: DivisionConfig[];
  selections: Selections;
  devices: Devices;
  floors: Floor[];
  activeFloorId: string | null;
  activeDivisionId: string | null;
  globalColor: string | null; // bulk colour applied to all colourable selections
  roomPlacements: RoomPlacements; // divId → key → {x,y} for 2D room editor
  roomDecor: RoomDecor;           // divId → furniture/existing markers (#5)
  roomSurveyNote: RoomSurveyNote; // divId → free-text "algo que devamos saber"

  setSessionId: (id: string | null) => void;
  setAccessToken: (token: string | null) => void;
  setWizardType: (type: WizardType) => void;
  setInstallTier: (tier: InstallTier) => void;
  setHasNeutral: (answer: NeutralAnswer | null) => void;
  setDivisions: (divisions: DivisionConfig[]) => void;
  toggleDivision: (divId: string) => void;
  setActiveDivision: (divId: string | null) => void;
  addSelection: (divId: string, sku: string) => void;
  removeSelection: (divId: string, sku: string) => void;
  toggleSelection: (divId: string, sku: string) => void;
  setGlobalColor: (color: string | null) => void;
  // Swap SKUs across every division at once (colour variant exchange).
  replaceSelections: (mapping: Record<string, string>) => void;
  // Device combos (guided add-device wizard)
  addDevice: (divId: string, combo: DeviceCombo) => void;
  removeDevice: (divId: string, comboId: string) => void;
  updateDeviceQty: (divId: string, comboId: string, qty: number) => void;
  // Ungroup ("Separar"): drop the combo grouping but KEEP its SKUs as standalone
  // selections so they become individual pins on the map.
  ungroupDevice: (divId: string, comboId: string) => void;
  // Floor plan
  addFloor: (label?: string) => void;
  removeFloor: (floorId: string) => void;
  setActiveFloor: (floorId: string | null) => void;
  setFloorHasPlan: (floorId: string, hasPlan: boolean) => void;
  setPlacement: (floorId: string, divId: string, pos: FloorPlacement) => void;
  removePlacement: (floorId: string, divId: string) => void;
  reset: () => void;
  // 2D room editor — product positions within a division
  setRoomProductPlacement: (divId: string, sku: string, pos: FloorPlacement) => void;
  removeRoomProductPlacement: (divId: string, sku: string) => void;
  clearRoomPlacements: (divId: string) => void;
  // Space annotation (#5) — furniture + existing-conditions markers
  addDecor: (divId: string, item: Omit<DecorItem, 'id'>) => void;
  moveDecor: (divId: string, id: string, pos: FloorPlacement) => void;
  removeDecor: (divId: string, id: string) => void;
  setSurveyNote: (divId: string, note: string) => void;
  // Division management
  addDivision: (div: DivisionConfig) => void;
  renameDivision: (divId: string, label: string) => void;
}

const initial: Pick<
  WizardState,
  | 'sessionId' | 'accessToken' | 'wizardType' | 'installTier' | 'hasNeutral' | 'divisions' | 'selections'
  | 'devices' | 'floors' | 'activeFloorId' | 'activeDivisionId' | 'globalColor'
  | 'roomPlacements' | 'roomDecor' | 'roomSurveyNote'
> = {
  sessionId: null,
  accessToken: null,
  wizardType: null,
  installTier: 'material',
  hasNeutral: null,
  divisions: [],
  selections: {},
  devices: {},
  floors: [],
  activeFloorId: null,
  activeDivisionId: null,
  globalColor: null,
  roomPlacements: {},
  roomDecor: {},
  roomSurveyNote: {},
};

// Debounced localStorage adapter — batches writes every 500ms.
// P0-6: localStorage (not sessionStorage) so the configuration survives closing
// the tab/window, matching the landing promise "as tuas escolhas ficam guardadas
// neste browser". Photos/plans still live in IndexedDB (idb-keyval). A migration
// below also recovers any in-progress config left in the old sessionStorage key.
const timers = new Map<string, ReturnType<typeof setTimeout>>();

const debouncedLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      const v = localStorage.getItem(name);
      if (v != null) return v;
      // One-time pickup of a config left behind in sessionStorage (pre-P0-6).
      const legacy = sessionStorage.getItem(name);
      if (legacy != null) {
        try { localStorage.setItem(name, legacy); sessionStorage.removeItem(name); } catch { /* noop */ }
      }
      return legacy;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    clearTimeout(timers.get(name));
    timers.set(
      name,
      setTimeout(() => {
        try {
          localStorage.setItem(name, value);
        } catch { /* quota or SSR */ }
        timers.delete(name);
      }, 500),
    );
  },
  removeItem: (name) => {
    clearTimeout(timers.get(name));
    timers.delete(name);
    try {
      localStorage.removeItem(name);
    } catch { /* noop */ }
  },
};

export const useWizardStore = create<WizardState>()(
  persist(
    (set, get) => ({
      ...initial,

      setSessionId: (id) => set({ sessionId: id }),
      setAccessToken: (token) => set({ accessToken: token }),
      setWizardType: (type) => set({ wizardType: type }),
      setInstallTier: (tier) => set({ installTier: tier }),
      setHasNeutral: (answer) => set({ hasNeutral: answer }),
      setDivisions: (divisions) => set({ divisions }),

      toggleDivision: (divId) =>
        set((s) => ({
          divisions: s.divisions.map((d) =>
            d.id === divId ? { ...d, optedIn: !d.optedIn } : d,
          ),
        })),

      setActiveDivision: (divId) => set({ activeDivisionId: divId }),

      addSelection: (divId, sku) =>
        set((s) => {
          const current = s.selections[divId] ?? [];
          if (current.includes(sku)) return s;
          return { selections: { ...s.selections, [divId]: [...current, sku] } };
        }),

      removeSelection: (divId, sku) =>
        set((s) => ({
          selections: {
            ...s.selections,
            [divId]: (s.selections[divId] ?? []).filter((s) => s !== sku),
          },
        })),

      toggleSelection: (divId, sku) => {
        const { selections, addSelection, removeSelection } = get();
        const current = selections[divId] ?? [];
        if (current.includes(sku)) removeSelection(divId, sku);
        else addSelection(divId, sku);
      },

      setGlobalColor: (color) => set({ globalColor: color }),

      replaceSelections: (mapping) =>
        set((s) => ({
          selections: Object.fromEntries(
            Object.entries(s.selections).map(([divId, skus]) => [
              divId,
              // Swap then dedup — old and new SKU may both be selected.
              [...new Set(skus.map((sku) => mapping[sku] ?? sku))],
            ]),
          ),
          // Keep combo SKUs in sync with the colour swap so the floor-plan
          // legend and combo removal keep pointing at the right variants.
          devices: Object.fromEntries(
            Object.entries(s.devices).map(([divId, combos]) => [
              divId,
              combos.map((c) => ({
                ...c,
                skus: [...new Set(c.skus.map((sku) => mapping[sku] ?? sku))],
              })),
            ]),
          ),
        })),

      addDevice: (divId, combo) =>
        set((s) => {
          const current = s.selections[divId] ?? [];
          return {
            devices: { ...s.devices, [divId]: [...(s.devices[divId] ?? []), combo] },
            selections: {
              ...s.selections,
              [divId]: [...new Set([...current, ...combo.skus])],
            },
          };
        }),

      ungroupDevice: (divId, comboId) =>
        set((s) => {
          const combos = s.devices[divId] ?? [];
          if (!combos.some((c) => c.id === comboId)) return s;
          // Keep selections untouched (SKUs stay selected → become standalone
          // pins); just drop the grouping and any combo-keyed map placement.
          const divPlacements = { ...(s.roomPlacements[divId] ?? {}) };
          delete divPlacements[comboId];
          return {
            devices: { ...s.devices, [divId]: combos.filter((c) => c.id !== comboId) },
            roomPlacements: { ...s.roomPlacements, [divId]: divPlacements },
          };
        }),

      removeDevice: (divId, comboId) =>
        set((s) => {
          const combos = s.devices[divId] ?? [];
          const removed = combos.find((c) => c.id === comboId);
          const remaining = combos.filter((c) => c.id !== comboId);
          if (!removed) return s;
          // Drop the combo's SKUs unless another combo in this division
          // still uses them. Manually-toggled duplicates are rare enough
          // that reclaiming them here is acceptable.
          const stillUsed = new Set(remaining.flatMap((c) => c.skus));
          return {
            devices: { ...s.devices, [divId]: remaining },
            selections: {
              ...s.selections,
              [divId]: (s.selections[divId] ?? []).filter(
                (sku) => !removed.skus.includes(sku) || stillUsed.has(sku),
              ),
            },
          };
        }),

      updateDeviceQty: (divId, comboId, qty) =>
        set((s) => {
          const combos = s.devices[divId] ?? [];
          if (qty <= 0) {
            // Treat qty=0 as remove
            const removed = combos.find((c) => c.id === comboId);
            const remaining = combos.filter((c) => c.id !== comboId);
            if (!removed) return s;
            const stillUsed = new Set(remaining.flatMap((c) => c.skus));
            return {
              devices: { ...s.devices, [divId]: remaining },
              selections: {
                ...s.selections,
                [divId]: (s.selections[divId] ?? []).filter(
                  (sku) => !removed.skus.includes(sku) || stillUsed.has(sku),
                ),
              },
            };
          }
          return {
            devices: {
              ...s.devices,
              [divId]: combos.map((c) => c.id === comboId ? { ...c, qty } : c),
            },
          };
        }),

      addFloor: (label) =>
        set((s) => {
          const id = `piso-${s.floors.length === 0 ? 0 : Math.max(...s.floors.map((f) => Number(f.id.split('-')[1] ?? 0))) + 1}`;
          const floor: Floor = {
            id,
            label: label ?? (s.floors.length === 0 ? 'R/C' : `${s.floors.length}º andar`),
            hasPlan: false,
            placements: {},
          };
          return { floors: [...s.floors, floor], activeFloorId: id };
        }),

      removeFloor: (floorId) =>
        set((s) => {
          const floors = s.floors.filter((f) => f.id !== floorId);
          return {
            floors,
            activeFloorId:
              s.activeFloorId === floorId ? (floors[0]?.id ?? null) : s.activeFloorId,
          };
        }),

      setActiveFloor: (floorId) => set({ activeFloorId: floorId }),

      setFloorHasPlan: (floorId, hasPlan) =>
        set((s) => ({
          floors: s.floors.map((f) => (f.id === floorId ? { ...f, hasPlan } : f)),
        })),

      setPlacement: (floorId, divId, pos) =>
        set((s) => ({
          floors: s.floors.map((f) =>
            f.id === floorId
              ? { ...f, placements: { ...f.placements, [divId]: pos } }
              : f,
          ),
        })),

      removePlacement: (floorId, divId) =>
        set((s) => ({
          floors: s.floors.map((f) => {
            if (f.id !== floorId) return f;
            const { [divId]: _gone, ...rest } = f.placements;
            return { ...f, placements: rest };
          }),
        })),

      reset: () => set({ ...initial }),

      setRoomProductPlacement: (divId, sku, pos) =>
        set((s) => ({
          roomPlacements: {
            ...s.roomPlacements,
            [divId]: { ...(s.roomPlacements[divId] ?? {}), [sku]: pos },
          },
        })),

      removeRoomProductPlacement: (divId, sku) =>
        set((s) => {
          const divPlacements = { ...(s.roomPlacements[divId] ?? {}) };
          delete divPlacements[sku];
          return { roomPlacements: { ...s.roomPlacements, [divId]: divPlacements } };
        }),

      clearRoomPlacements: (divId) =>
        set((s) => {
          const { [divId]: _gone, ...rest } = s.roomPlacements;
          return { roomPlacements: rest };
        }),

      addDecor: (divId, item) =>
        set((s) => {
          const id = `decor-${Date.now().toString(36)}-${(s.roomDecor[divId]?.length ?? 0)}`;
          return {
            roomDecor: {
              ...s.roomDecor,
              [divId]: [...(s.roomDecor[divId] ?? []), { ...item, id }],
            },
          };
        }),

      moveDecor: (divId, id, pos) =>
        set((s) => ({
          roomDecor: {
            ...s.roomDecor,
            [divId]: (s.roomDecor[divId] ?? []).map((d) =>
              d.id === id ? { ...d, x: pos.x, y: pos.y } : d,
            ),
          },
        })),

      removeDecor: (divId, id) =>
        set((s) => ({
          roomDecor: {
            ...s.roomDecor,
            [divId]: (s.roomDecor[divId] ?? []).filter((d) => d.id !== id),
          },
        })),

      setSurveyNote: (divId, note) =>
        set((s) => ({
          roomSurveyNote: { ...s.roomSurveyNote, [divId]: note },
        })),

      addDivision: (div) =>
        set((s) => ({ divisions: [...s.divisions, div] })),

      renameDivision: (divId, label) =>
        set((s) => ({
          divisions: s.divisions.map((d) => (d.id === divId ? { ...d, label } : d)),
        })),
    }),
    {
      name: 'syhouse:wizard',
      version: 5,
      migrate: (persisted, fromVersion) => {
        const s = persisted as WizardState;
        if (fromVersion < 5 && (s as { hasNeutral?: NeutralAnswer }).hasNeutral === undefined) {
          (s as { hasNeutral: NeutralAnswer | null }).hasNeutral = null;
        }
        if (fromVersion < 2) return { ...s, roomPlacements: {} };
        if (fromVersion < 3) {
          // Add qty:1 to all existing DeviceCombos that predate the qty field
          const devices = Object.fromEntries(
            Object.entries(s.devices ?? {}).map(([divId, combos]) => [
              divId,
              (combos as DeviceCombo[]).map((c) => ('qty' in c ? c : { ...(c as object), qty: 1 } as DeviceCombo)),
            ]),
          );
          return { ...s, devices, roomDecor: {}, roomSurveyNote: {} };
        }
        if (fromVersion < 4) return { ...s, roomDecor: {}, roomSurveyNote: {} };
        return s;
      },
      storage: createJSONStorage(() => debouncedLocalStorage),
      partialize: (s) => ({
        sessionId: s.sessionId,
        accessToken: s.accessToken,
        wizardType: s.wizardType,
        installTier: s.installTier,
        hasNeutral: s.hasNeutral,
        divisions: s.divisions,
        selections: s.selections,
        devices: s.devices,
        floors: s.floors,
        activeFloorId: s.activeFloorId,
        activeDivisionId: s.activeDivisionId,
        globalColor: s.globalColor,
        roomPlacements: s.roomPlacements,
        roomDecor: s.roomDecor,
        roomSurveyNote: s.roomSurveyNote,
      }),
    },
  ),
);
